import { Fragment, useState, useCallback, useEffect } from 'react';
import classnames from 'classnames/bind';
import { Header, Text, Button, Input, Actions, Link } from '@gasbuddy/react-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styles from './EditMemberNameStatus.module.css';
import { isValidMemberName, noop } from '../../../lib/utils';

const cx = classnames.bind(styles);

export const USERNAME_STATE = {
  EDITABLE: 1,
  CUSTOMIZED: 2,
  UPDATED: 3,
};

export default function EditMemberNameStatus({
  error,
  isLoading,
  memberName,
  onClose,
  onSubmit,
  variant,
}) {
  const { t } = useTranslation(['account', 'miscellaneous']);
  const [userName, setUserName] = useState(memberName || '');
  const [userNameError, setUserNameError] = useState();

  useEffect(() => {
    setUserNameError(error);
  }, [error]);

  const onUserNameChange = useCallback((e) => {
    e.preventDefault();
    setUserNameError(undefined);
    setUserName(e.target.value);
  }, []);

  const handleOnSubmit = useCallback(() => {
    if (userName.length < 5 || userName.length > 15) {
      setUserNameError(t('usernameLengthError', { ns: 'account' }));
      return;
    }

    if (!isValidMemberName(userName)) {
      setUserNameError(t('usernameSpecialCharsError', { ns: 'account' }));
      return;
    }
    onSubmit(userName);
  }, [userName, onSubmit, t]);

  const isDisabled = isLoading || !userName;

  switch (variant) {
    case USERNAME_STATE.EDITABLE: {
      return (
        <Fragment>
          <Header as="h3">
            {t('editYourUsername', { ns: 'account' })}
          </Header>
          <Text as="p" className={cx('textSpacer')}>
            {t('chooseAUsername', { ns: 'account' })}
          </Text>
          <Input
            className={cx('memberNameInput')}
            data-testid="newMemberNameInput"
            value={userName}
            onChange={onUserNameChange}
            error={userNameError}
            placeholder={t('enterNewUsername', { ns: 'account' })}
          />
          <br />
          <Actions>
            <Actions.Button
              primary
              fluid
              onClick={handleOnSubmit}
              loading={isLoading}
              disabled={isDisabled}
              desktop={6}
            >
              {t('setUsername', { ns: 'account' })}
            </Actions.Button>
            <Actions.Action desktop={4}>
              <Link onClick={onClose} uppercase>
                {t('actions.cancel', { ns: 'miscellaneous' })}
              </Link>
            </Actions.Action>
          </Actions>
          <br />
          <Text as="p" size="sm">
            {t('usernameRefreshNote', { ns: 'account' })}
          </Text>
        </Fragment>
      );
    }

    case USERNAME_STATE.UPDATED: {
      return (
        <Fragment>
          <Header as="h3">
            {t('usernameUpdated', { ns: 'account' })}
          </Header>
          <Text as="p">
            {t('usernameNiceRing', { ns: 'account', username: userName })}
          </Text>
          <Text as="p">
            {t('pageWillRefresh', { ns: 'account' })}
          </Text>
        </Fragment>
      );
    }

    case USERNAME_STATE.CUSTOMIZED: {
      return (
        <Fragment>
          <Header as="h3">
            {t('alreadySetUsername', { ns: 'account' })}
          </Header>
          <Text as="p">
            {t('usernameOnceOnly', { ns: 'account' })}
          </Text>
          <br />
          <Button primary wide onClick={onClose}>{t('actions.okay', { ns: 'miscellaneous' })}</Button>
        </Fragment>
      );
    }

    default: {
      return false;
    }
  }
}

EditMemberNameStatus.propTypes = {
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  memberName: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  variant: PropTypes.oneOf(Object.values(USERNAME_STATE)),
};

EditMemberNameStatus.defaultProps = {
  error: undefined,
  isLoading: false,
  memberName: undefined,
  onClose: noop,
  onSubmit: noop,
  variant: undefined,
};
